import { useMemo, useState } from 'react';
import { AlertCircle, RefreshCw, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { useAdminPayments, useApartmentSummaries } from '../hooks/usePayments';
import { useApartments } from '../hooks/useApartments';
import { bookingQueryOptions } from '../hooks/useBookings';
import { useSupportCaseList } from '../hooks/useSupportCases';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { PaymentDetailPanel } from '../components/PaymentDetailPanel';
import { SupportCaseIndicator } from '../components/SupportCaseIndicator';
import { ListRefreshButton } from '../components/ListRefreshButton';
import { formatDate } from '../utils/formatDate';
import type { AdminPaymentSummary, PaymentListFilter } from '../types/payment';
import { isAuthRequiredError } from '../utils/listRefreshState';
import { NON_FINAL_SUPPORT_CASE_STATUSES } from '../types/supportCase';

// ── Helpers ──────────────────────────────────────────────────────────────────

const CURRENCY_SYMBOLS: Record<string, string> = { eur: '€', usd: '$', gbp: '£' };
const currencySymbol = (code: string) => CURRENCY_SYMBOLS[code.toLowerCase()] ?? code.toUpperCase();

/** Format a numeric amount with a currency symbol. */
const formatAmount = (amount: number, currency: string): string =>
  `${currencySymbol(currency)}${amount.toFixed(2)}`;

/** Render a Record<currency, total> as one formatted line per currency, or €0.00 when empty. */
const formatTotals = (record: Record<string, number>): string => {
  const entries = Object.entries(record);
  if (entries.length === 0) return '€0.00';
  return entries
    .map(([code, amount]) => `${currencySymbol(code)}${amount.toFixed(2)}`)
    .join(' / ');
};

interface PaymentSummaryTotals {
  succeeded: Record<string, number>;
  inFlight:  Record<string, number>;
  failed:    Record<string, number>;
}

/** Compute per-currency totals from the current page's content array. */
const computeTotals = (payments: readonly AdminPaymentSummary[]): PaymentSummaryTotals => {
  const result: PaymentSummaryTotals = { succeeded: {}, inFlight: {}, failed: {} };

  for (const p of payments) {
    const key = p.currency.toLowerCase();
    if (p.status === 'SUCCEEDED') {
      result.succeeded[key] = (result.succeeded[key] ?? 0) + p.amount;
    } else if (p.status === 'INITIATED') {
      result.inFlight[key] = (result.inFlight[key] ?? 0) + p.amount;
    } else if (p.status === 'FAILED') {
      result.failed[key] = (result.failed[key] ?? 0) + p.amount;
    }
    // EXPIRED is intentionally excluded from all summary buckets
  }

  return result;
};

// ── Component ─────────────────────────────────────────────────────────────────

const Payments = () => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [apartmentName,  setApartmentName]  = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const [guestEmail,     setGuestEmail]     = useState('');
  const [dateFrom,       setDateFrom]       = useState('');
  const [dateTo,         setDateTo]         = useState('');

  // ── Pagination state ──────────────────────────────────────────────────────
  const [page, setPage] = useState(0);

  // ── Selected payment for detail panel ────────────────────────────────────
  const [selectedPayment, setSelectedPayment] = useState<AdminPaymentSummary | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  // ── Build filter — omit status (client-side below) and blank fields ────────
  const filter: PaymentListFilter = {
    ...(apartmentName  ? { apartmentName }                           : {}),
    ...(guestEmail     ? { guestEmail }                              : {}),
    ...(dateFrom       ? { createdAtFrom: `${dateFrom}T00:00:00Z` } : {}),
    ...(dateTo         ? { createdAtTo:   `${dateTo}T23:59:59Z`   } : {}),
  };

  // ── Data fetching ──────────────────────────────────────────────────────────
  const { data, isLoading, isError, isFetching, refetch } = useAdminPayments(filter, page);
  const { data: apartmentSummaries = [] }      = useApartmentSummaries();

  // ── Support case join (T042) ───────────────────────────────────────────────
  const { data: apartments = [] } = useApartments();
  const bookingQueries = useQueries({
    queries: apartments.map(apt => bookingQueryOptions(apt.id)),
  });
  const allBookings = bookingQueries.flatMap(q => q.data ?? []);

  const { data: nonFinalCasesPage } = useSupportCaseList({
    statuses: [...NON_FINAL_SUPPORT_CASE_STATUSES],
    sort: 'openedAt,desc',
  });
  const nonFinalCases = useMemo(() => nonFinalCasesPage?.content ?? [], [nonFinalCasesPage]);

  // Map: normalized email → active SupportCase (only when exactly one booking matches)
  const caseByEmail = useMemo(() => {
    const emailToBookingIds = new Map<string, string[]>();
    for (const b of allBookings) {
      const email = b.guestInfo.email.toLowerCase();
      const ids = emailToBookingIds.get(email) ?? [];
      ids.push(b.bookingId);
      emailToBookingIds.set(email, ids);
    }
    const caseByBookingId = new Map(nonFinalCases.map(c => [c.bookingId, c]));
    const result = new Map<string, (typeof nonFinalCases)[0]>();
    for (const [email, bookingIds] of emailToBookingIds) {
      const matchingCases = bookingIds
        .map(id => caseByBookingId.get(id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined);
      if (matchingCases.length === 1) {
        result.set(email, matchingCases[0]);
      }
    }
    return result;
  }, [allBookings, nonFinalCases]);

  const content = data?.content ?? [];

  const visibleRows = statusFilter
    ? content.filter(r => r.status === statusFilter)
    : content;

  const totalPages   = data?.totalPages    ?? 1;
  const currentPage  = data?.currentPage   ?? 0;

  const summaryTotals = computeTotals(visibleRows);

  const isFilterActive = !!(apartmentName || statusFilter || guestEmail || dateFrom || dateTo);

  const clearFilters = () => {
    setApartmentName('');
    setStatusFilter('');
    setGuestEmail('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const handleRetry = () => void refetch();

  const handleRefresh = async () => {
    try {
      setRefreshError(null);
      await refetch({ throwOnError: true });
    } catch (error) {
      setRefreshError(isAuthRequiredError(error) ? t('common:signInRequired') : t('common:refreshFailed'));
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('pages:paymentsHeading')}</h1>
        <p className="text-gray-600 mt-2">{t('pages:paymentsSubHeading')}</p>
      </div>

      <div className="flex justify-end mb-4">
        <ListRefreshButton isRefreshing={isFetching} onRefresh={handleRefresh} />
      </div>

      {refreshError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{refreshError}</span>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1 text-sm font-medium hover:text-red-900 transition-colors"
          >
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {/* ── Error banner ────────────────────────────────────────────────── */}
      {isError && (
        <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle size={18} className="shrink-0" />
          <span className="flex-1 text-sm">{t('errors:failedToLoadPayments')}</span>
          <button
            onClick={handleRetry}
            className="flex items-center gap-1 text-sm font-medium hover:text-red-900 transition-colors"
          >
            <RefreshCw size={14} />
            {t('common:retry')}
          </button>
        </div>
      )}

      {/* ── Summary cards ───────────────────────────────────────────────── */}
      {!isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card" data-testid="summary-succeeded">
            <p className="text-sm text-gray-500">{t('pages:totalCollected')}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatTotals(summaryTotals.succeeded)}</p>
          </div>
          <div className="card" data-testid="summary-inflight">
            <p className="text-sm text-gray-500">{t('pages:inFlight')}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatTotals(summaryTotals.inFlight)}</p>
          </div>
          <div className="card" data-testid="summary-failed">
            <p className="text-sm text-gray-500">{t('badges:statusFailed')}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatTotals(summaryTotals.failed)}</p>
          </div>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      {!isLoading && (
        <div className="flex flex-wrap gap-3 mb-6">
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
            className="filter-control"
            aria-label={t('common:allPaymentStatuses')}
          >
            <option value="">{t('common:allStatuses')}</option>
            <option value="SUCCEEDED">{t('badges:statusPaid')}</option>
            <option value="INITIATED">{t('badges:statusPending')}</option>
            <option value="FAILED">{t('badges:statusFailed')}</option>
            <option value="EXPIRED">{t('badges:statusExpired')}</option>
          </select>

          <select
            value={apartmentName}
            onChange={e => { setApartmentName(e.target.value); setPage(0); }}
            className="filter-control"
            aria-label={t('common:allApartments')}
          >
            <option value="">{t('common:allApartments')}</option>
            {apartmentSummaries.map(apt => (
              <option key={apt.id} value={apt.name}>{apt.name}</option>
            ))}
          </select>

          <label className="sr-only" htmlFor="guest-email">{t('pages:guestEmailPlaceholder')}</label>
          <input
            id="guest-email"
            type="email"
            value={guestEmail}
            onChange={e => { setGuestEmail(e.target.value); setPage(0); }}
            placeholder={t('pages:guestEmailPlaceholder')}
            className="filter-control"
            aria-label={t('pages:guestEmailPlaceholder')}
          />

          <label className="sr-only" htmlFor="date-from">Date from</label>
          <input
            id="date-from"
            type="date"
            value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setPage(0); }}
            className="filter-control"
            aria-label="Date from"
          />

          <label className="sr-only" htmlFor="date-to">Date to</label>
          <input
            id="date-to"
            type="date"
            value={dateTo}
            onChange={e => { setDateTo(e.target.value); setPage(0); }}
            className="filter-control"
            aria-label="Date to"
          />

          {isFilterActive && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <XCircle size={14} />
              {t('common:clearFilters')}
            </button>
          )}
        </div>
      )}

      {/* ── Loading ──────────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="card flex justify-center py-16">
          <span className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────────────────── */}
      {!isLoading && (
        <>
          {visibleRows.length === 0 ? (
            <div className="card">
              <p className="text-gray-600 text-center py-12">
                {isFilterActive
                  ? t('pages:noPaymentsMatchFilters')
                  : t('pages:noPaymentsFound')}
              </p>
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 text-xs uppercase tracking-wide">
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnApartment')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnGuest')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnEmail')}</th>
                    <th className="pb-3 pr-4 font-medium text-right">{t('pages:fieldAmount')}</th>
                    <th className="pb-3 pr-4 font-medium">{t('pages:columnStatus')}</th>
                    <th className="pb-3 font-medium">{t('pages:fieldCreated')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleRows.map(row => (
                    <tr
                      key={row.paymentId}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedPayment(row)}
                    >
                      <td className="py-3 pr-4 font-medium text-gray-900">{row.apartmentName}</td>
                      <td className="py-3 pr-4 text-gray-700">
                        {row.guestFirstName} {row.guestLastName}
                      </td>
                      <td className="py-3 pr-4 text-gray-600">{row.guestEmail}</td>
                      <td className="py-3 pr-4 text-gray-700 text-right font-medium">
                        {formatAmount(row.amount, row.currency)}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <PaymentStatusBadge status={row.status} />
                          {(() => {
                            const activeCase = caseByEmail.get(row.guestEmail.toLowerCase());
                            return activeCase
                              ? <SupportCaseIndicator caseId={activeCase.caseId} status={activeCase.status} />
                              : null;
                          })()}
                        </div>
                      </td>
                      <td className="py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(row.createdAt.slice(0, 10))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ───────────────────────────────────────────────── */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>
                {t('common:pageOf', { current: currentPage + 1, total: totalPages })}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label={t('common:previousPage')}
                >
                  <ChevronLeft size={14} />
                  {t('common:previous')}
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label={t('common:nextPage')}
                >
                  {t('common:next')}
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Detail panel ─────────────────────────────────────────────────── */}
      <PaymentDetailPanel
        payment={selectedPayment}
        onClose={() => setSelectedPayment(null)}
      />
    </div>
  );
};

export default Payments;
